import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { LoadingSpinner } from "./loading"

export const buttonVariants = cva(
  "inline-flex items-center cursor-pointer justify-center whitespace-nowrap group rounded-md text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-primary underline-offset-4 hover:underline",
        operator: 'w-full',
        operatorPrimary: 'w-full',
        tab: 'bg-transparent data-[active=true]:bg-primary text-foreground data-[active=true]:text-white w-full'
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
      operator:{
        'irancell': 'data-[active=true]:bg-irancell-secondary data-[active=true]:text-irancell-primary bg-background/50 backdrop-blur-xs',
        'td-lte': 'data-[active=true]:bg-irancell-secondary data-[active=true]:text-irancell-primary bg-background/50 backdrop-blur-xs',
        'mci': 'data-[active=true]:bg-mci-secondary data-[active=true]:text-white bg-background/50 backdrop-blur-xs',
        'rightel': 'data-[active=true]:bg-rightel-secondary data-[active=true]:text-rightel-primary bg-background/50 backdrop-blur-xs',
        'shatel': 'data-[active=true]:bg-shatel-secondary data-[active=true]:text-shatel-primary bg-background/50 backdrop-blur-xs',
        'aptel': 'data-[active=true]:bg-aptel-secondary data-[active=true]:text-aptel-primary bg-background/50 backdrop-blur-xs',
        'default': '',
      },
      operatorPrimary:{
        'irancell': 'data-[active=true]:bg-irancell-primary data-[active=true]:text-irancell-secondary bg-background/50 backdrop-blur-xs',
        'td-lte': 'data-[active=true]:bg-irancell-primary data-[active=true]:text-irancell-secondary bg-background/50 backdrop-blur-xs',
        'mci': 'data-[active=true]:bg-mci-primary data-[active=true]:text-white bg-background/50 backdrop-blur-xs',
        'rightel': 'data-[active=true]:bg-rightel-primary data-[active=true]:text-rightel-secondary bg-background/50 backdrop-blur-xs',
        'shatel': 'data-[active=true]:bg-shatel-primary data-[active=true]:text-shatel-secondary bg-background/50 backdrop-blur-xs',
        'aptel': 'data-[active=true]:bg-aptel-primary data-[active=true]:text-aptel-secondary bg-background/50 backdrop-blur-xs',
        'default': '',
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size,operator,operatorPrimary, asChild = false, isLoading, children, disabled , ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className, operator, operatorPrimary }))}
        ref={ref}
        disabled={isLoading || disabled}
        {...props}
      >
        {isLoading ? <LoadingSpinner /> : children}
        </Comp>
    )
  }
)
Button.displayName = "Button"

export { Button,  }
